import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { SafeVendorUser, VendorUserType } from "@shared/schema";

// Task #936 — Vendor portal auth context. Mirrors `use-agent-auth`
// but for the economist / business-plan-writer portal seats.

interface VendorMePayload {
  seat: SafeVendorUser;
  vendor: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    firmName: string | null;
    email: string | null;
  };
  /** Task #1421 — true when the vendor has no acceptance row for the
   * current terms version, surfaced by GET /api/vendor/me. */
  needsTermsAcceptance?: boolean;
}

interface VendorAuthContextType {
  seat: SafeVendorUser | null;
  vendor: VendorMePayload["vendor"] | null;
  vendorType: VendorUserType | null;
  isLoading: boolean;
  /** Task #1421 — true when the vendor needs to accept the current terms. */
  needsTermsAcceptance: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const VendorAuthContext = createContext<VendorAuthContextType | null>(null);

export function VendorAuthProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery<VendorMePayload | null>({
    queryKey: ["/api/vendor/me"],
    staleTime: Infinity,
    retry: false,
  });

  const seat = data?.seat ?? null;
  const vendor = data?.vendor ?? null;
  const vendorType = (seat?.vendorType ?? null) as VendorUserType | null;
  const needsTermsAcceptance: boolean = data?.needsTermsAcceptance === true;

  useEffect(() => {
    if (seat) {
      Sentry.setUser({ id: seat.id, role: "vendor" });
      Sentry.setTag("role", "vendor");
      if (vendorType) {
        Sentry.setTag("vendorType", vendorType);
      }
    } else if (data === null) {
      Sentry.setUser(null);
      Sentry.setTag("role", null);
    }
  }, [seat, vendorType, data]);

  const resetCache = async () => {
    await queryClient.cancelQueries();
    queryClient.setQueryData(["/api/vendor/me"], null);
    queryClient.removeQueries({
      predicate: (q) => q.queryKey[0] !== "/api/vendor/me",
    });
  };

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      await apiRequest("POST", "/api/vendor/login", { email, password });
    },
    onSuccess: async () => {
      await resetCache();
      await queryClient.invalidateQueries({ queryKey: ["/api/vendor/me"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/vendor/logout", {});
    },
    onSuccess: async () => {
      await resetCache();
    },
  });

  return (
    <VendorAuthContext.Provider
      value={{
        seat,
        vendor,
        vendorType,
        isLoading,
        needsTermsAcceptance,
        login: async (email, password) => {
          await loginMutation.mutateAsync({ email, password });
        },
        logout: async () => {
          await logoutMutation.mutateAsync();
        },
      }}
    >
      {children}
    </VendorAuthContext.Provider>
  );
}

export function useVendorAuth() {
  const ctx = useContext(VendorAuthContext);
  if (!ctx) throw new Error("useVendorAuth must be used within VendorAuthProvider");
  return ctx;
}
